import React, { useState } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Drawer, 
  List, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  IconButton,
  styled,
  alpha,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
  Badge,
  Avatar,
} from '@mui/material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CategoryIcon from '@mui/icons-material/Category';
import ArticleIcon from '@mui/icons-material/Article';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ImageIcon from '@mui/icons-material/Image';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useAdmin } from '../../hooks/useAdmin';
import Img from '../../logob.png';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import FolderIcon from '@mui/icons-material/Folder';
import { useThemeMode } from '../../App';

const drawerWidth = 280;

const Logo = styled('img')(({ theme }) => ({
  height: '40px',
  width: 'auto',
  transition: 'transform 0.3s ease',
  filter: theme.palette.mode === 'dark' ? 'brightness(1.2)' : 'none',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(12px)',
  boxShadow: 'none',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  color: theme.palette.text.primary,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const NavIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  transition: 'transform 0.2s ease, color 0.2s ease',
  '&:hover': {
    color: theme.palette.primary.main,
    transform: 'translateY(-2px)',
  },
}));

const adminMenuItems = [
  {
    id: 'dashboard',
    title: 'Главная',
    icon: <DashboardIcon />,
    path: '/admin/panel'
  },
  {
    id: 'users',
    title: 'Пользователи',
    icon: <PeopleIcon />,
    subItems: [
      {
        id: 'create-user',
        title: 'Создать',
        icon: <PersonAddIcon />,
        path: '/admin/panel/users/create'
      },
      {
        id: 'all-users',
        title: 'Все пользователи',
        icon: <PeopleIcon />,
        path: '/admin/panel/users/list'
      }
    ]
  },
  {
    id: 'content',
    title: 'Категории',
    icon: <CategoryIcon />,
    subItems: [
      {
        id: 'categories',
        title: 'Категории',
        icon: <CategoryIcon />,
        path: '/admin/panel/categories'
      },
      {
        id: 'subcategories',
        title: 'Подкатегории',
        icon: <FolderIcon />,
        path: '/admin/panel/subcategories'
      },
      {
        id: 'category-settings',
        title: 'Настройки',
        icon: <SettingsIcon />,
        path: '/admin/panel/category-settings'
      }
    ]
  },
  {
    id: 'articles',
    title: 'Статьи',
    icon: <ArticleIcon />,
    path: '/admin/panel/articles'
  },
  {
    id: 'media',
    title: 'Медиа',
    icon: <ImageIcon />,
    path: '/admin/panel/media'
  }
];

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, userRole, userId } = useAdmin('/admin/auth');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenuItems, setOpenMenuItems] = useState<{ [key: string]: boolean }>({
    content: false
  });
  const { mode, toggleTheme } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [notifications] = useState(2); // Пример уведомлений

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuItemClick = (itemId: string) => {
    setOpenMenuItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const filteredMenuItems = adminMenuItems.filter(item => {
    if (userRole === 'editor' && item.id === 'users') {
      return false;
    }
    return true;
  });

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: theme.palette.background.paper
    }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'center', px: 2 }}>
        <Logo 
          src={Img}
          alt="Zerde"
          onClick={() => handleNavigation('/admin/panel')}
          sx={{ cursor: 'pointer', width: '100%', height: '90%' }}
        />
      </Toolbar>
      
      <Box sx={{ px: 3, py: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 1.5 
        }}>
          <Avatar 
            sx={{ 
              bgcolor: theme.palette.primary.main,
              width: 40,
              height: 40,
              boxShadow: `0 0 0 2px ${alpha(theme.palette.background.paper, 0.8)}`
            }}
          >
            {userRole === 'admin' ? 'A' : 'E'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {userRole === 'admin' ? 'Администратор' : 'Редактор'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {userId}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 2, opacity: 0.6 }} />
      
      <List sx={{ flex: 1, overflow: 'auto', px: 2 }}>
        {filteredMenuItems.map((item) => (
          <React.Fragment key={item.id}>
            <ListItemButton 
              onClick={() => item.subItems 
                ? handleMenuItemClick(item.id)
                : handleNavigation(item.path || '')
              }
              selected={item.path ? location.pathname === item.path : false}
              sx={{
                mb: 0.5,
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  transform: 'translateX(4px)',
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  },
                },
              }}
            >
              <ListItemIcon sx={{
                color: location.pathname === item.path
                  ? 'primary.main'
                  : 'text.secondary'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.title} />
              {item.subItems && (
                openMenuItems[item.id] ? <ExpandLess /> : <ExpandMore />
              )}
            </ListItemButton>
            {item.subItems && (
              <Collapse in={openMenuItems[item.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.id}
                      onClick={() => handleNavigation(subItem.path)}
                      selected={location.pathname === subItem.path}
                      sx={{
                        pl: 4,
                        mb: 0.5,
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          transform: 'translateX(4px)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.15),
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{
                        color: location.pathname === subItem.path
                          ? 'primary.main'
                          : 'text.secondary'
                      }}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.title} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
      
      <Box sx={{ p: 2 }}>
        <Divider sx={{ mb: 2, opacity: 0.6 }} />
        <Tooltip title="Перейти на сайт">
          <ListItemButton
            onClick={() => window.open('/', '_blank')}
            sx={{
              borderRadius: 2,
              mb: 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                transform: 'translateX(4px)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'text.secondary' }}>
              <ArticleIcon />
            </ListItemIcon>
            <ListItemText primary="Перейти на сайт" />
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <StyledAppBar position="fixed">
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Logo 
              src={Img} 
              alt="Zerde Guide"
              onClick={() => navigate('/admin/panel')}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              ml: { xs: 2, sm: 0 }, 
              flexGrow: 1,
              fontWeight: 600,
            }}
          >
            Панель администратора
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Когда нибудь будет">
              <NavIconButton sx={{ mr: 1 }}>
                <Badge badgeContent={notifications} color="error">
                  <NotificationsNoneIcon />
                </Badge>
              </NavIconButton>
            </Tooltip>
            
            <Tooltip title={mode === 'light' ? 'Тёмная тема' : 'Светлая тема'}>
              <NavIconButton onClick={toggleTheme} sx={{ mr: 1 }}>
                {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
              </NavIconButton>
            </Tooltip>
          
            <Tooltip title="Выйти">
              <NavIconButton 
                onClick={logout}
                sx={{
                  '&:hover': {
                    color: 'error.main',
                  },
                }}
              >
                <LogoutIcon />
              </NavIconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </StyledAppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box className="fade-in">
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout; 